import React, { useState, useEffect } from 'react';
import { Icon } from '@iconify/react';
import { 
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  IconButton,
  Box,
  Typography,
  Paper,
  Grid,
  Chip,
  List,
  ListItem,
  ListItemText,
  Stack,
  TextField,
  Tabs,
  Tab,
  useTheme,
  useMediaQuery,
  CircularProgress
} from '@material-ui/core';
import { useSnackbar } from 'notistack5';

// api
import useRequiredDocuments from 'utils/api/requiredDocuments';
import storage from 'utils/storage';
import { logSuccessAction, ACTION_CATEGORIES } from 'utils/actionLogger';

// Document Review Modal Component
// Props:
// - open: boolean to control modal visibility
// - onClose: function to close modal
// - employeeId: ID of employee whose documents to review
// - company: company/store context for filtering documents (important for group store functionality)
export default function DocumentReviewModal({ open, onClose, employeeId, company }) {
    const [isLoading, setLoading] = useState(true);
    const [documents, setDocuments] = useState([]);
    const [missingDocuments, setMissingDocuments] = useState([]);
    const [documentTypeMap, setDocumentTypeMap] = useState({});
    const [openPreviewDialog, setOpenPreviewDialog] = useState(false);
    const [previewFile, setPreviewFile] = useState(null);
    const [currentDocument, setCurrentDocument] = useState(null);
    const [openRejectDialog, setOpenRejectDialog] = useState(false);
    const [rejectionReason, setRejectionReason] = useState('');
    const [isProcessing, setIsProcessing] = useState(false);
    const [tabValue, setTabValue] = useState(0);
    const { enqueueSnackbar } = useSnackbar();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  
    useEffect(() => {
      console.log("DocumentReviewModal - employeeId:", employeeId, "company:", company)
      if (open && employeeId && company) {
        loadDocuments();
      }
    }, [open, employeeId, company]);
  
    const loadDocuments = async () => {
      setLoading(true);
      try {
        // Get user data
        const local_user = await storage.getUser();
        if (!local_user) {
          onClose();
          return enqueueSnackbar('Unable to retrieve user data', { variant: 'error' });
        }
        
        // Get all required documents first to create the document type mapping
        // Use the company parameter passed to the modal for proper context
        const requiredDocsResponse = await useRequiredDocuments.getAllRequiredDocuments(company);
        if (!requiredDocsResponse.ok) {
          throw new Error(requiredDocsResponse.error || 'Failed to load required documents');
        }
        
        // Create a mapping of document ID to document name
        const docTypeMapping = {};
        requiredDocsResponse.data.forEach(doc => {
          docTypeMapping[doc._id] = doc.name;
        });
        setDocumentTypeMap(docTypeMapping);
        
        // Fetch documents for this specific employee using the API utility
        const response = await useRequiredDocuments.get_employee_documents(employeeId);
        if (response.status === 200) {
          // Filter out documents that don't have a matching required document type
          // Also filter by company context to ensure we only show documents for the selected store
          const validDocuments = response.data.filter(doc => {
            // Check if document has matching required document type
            const hasMatchingType = docTypeMapping[doc.requiredDocumentId];
            // For company context filtering, we'll rely on the backend to return appropriate documents
            // based on the employee's company association
            return hasMatchingType;
          });
          setDocuments(validDocuments);
          
          // Identify missing documents based on the required docs for this company context
          const requiredDocs = requiredDocsResponse.data;
          const uploadedDocTypes = new Set(validDocuments.map(doc => doc.requiredDocumentId));
          
          // Filter out documents that haven't been uploaded yet
          const missing = requiredDocs.filter(doc => !uploadedDocTypes.has(doc._id));
          setMissingDocuments(missing);
        } else {
          throw new Error(response.error || 'Failed to load documents');
        }
      } catch (error) {
        console.error('Error loading documents:', error);
        enqueueSnackbar('Failed to load employee documents', { variant: 'error' });
      } finally {
        setLoading(false);
      }
    };
  
    const handleTabChange = (event, newValue) => {
      setTabValue(newValue);
    };
  
    const handlePreview = (file) => {
      setPreviewFile(file);
      setOpenPreviewDialog(true);
    };
  
    const refreshDocumentList = async () => {
      try {
        loadDocuments();
      } catch (error) {
        console.error('Error refreshing document list:', error);
      }
    };
  
    const handleApprove = async (document) => {
      setIsProcessing(true);
      try {
        const local_user = await storage.getUser();
        if (!local_user) {
          onClose();
          return enqueueSnackbar('Unable to process action', { variant: 'error' });
        }
        const user = JSON.parse(local_user);
        
        // Use the API utility function for document approval
        const response = await useRequiredDocuments.approve_document({
          documentId: document._id,
          userId: user._id
        });
        
        if (response.status === 200) {
          // Update document in state
          const updatedDocuments = documents.map(doc => 
            doc._id === document._id ? { ...doc, status: 'approved', reviewedAt: new Date() } : doc
          );
          
          setDocuments(updatedDocuments);
          enqueueSnackbar('Document approved successfully', { variant: 'success' });
          
          // Log successful document approval with proper company context
          logSuccessAction({
            user: user._id,
            company: company || user.company, // Use modal company context or fallback to user company
            userDisplay: {
              name: user.displayName,
              email: user.email,
            },
            role: user.role,
            action: 'Approved Employee Document',
            category: ACTION_CATEGORIES.USER_MANAGEMENT,
            details: `Successfully approved document: ${getDocumentTypeName(document.requiredDocumentId)} for employee in store: ${company || user.company}.`
          });
          
          // Refresh document list
          await refreshDocumentList();
        } else {
          throw new Error(response.error || 'Failed to approve document');
        }
      } catch (error) {
        console.error('Error approving document:', error);
        enqueueSnackbar('Failed to approve document', { variant: 'error' });
      } finally {
        setIsProcessing(false);
      }
    };
  
    const handleOpenRejectDialog = (document) => {
      setCurrentDocument(document);
      setRejectionReason('');
      setOpenRejectDialog(true);
    };
  
    const handleCloseRejectDialog = () => {
      setOpenRejectDialog(false);
    };
  
    const handleReject = async () => {
      if (!rejectionReason.trim()) {
        return enqueueSnackbar('Please provide a rejection reason', { variant: 'warning' });
      }
      
      setIsProcessing(true);
      try {
        const local_user = await storage.getUser();
        if (!local_user) {
          onClose();
          return enqueueSnackbar('Unable to process action', { variant: 'error' });
        }
        const user = JSON.parse(local_user);
        
        // Use the API utility function for document rejection
        const response = await useRequiredDocuments.reject_document({
          documentId: currentDocument._id,
          rejectionReason: rejectionReason,
          userId: user._id
        });
        
        if (response.status === 200) {
          // Update document in state
          const updatedDocuments = documents.map(doc => 
            doc._id === currentDocument._id 
              ? { ...doc, status: 'rejected', rejectionReason, reviewedAt: new Date() } 
              : doc
          );
          
          setDocuments(updatedDocuments);
          enqueueSnackbar('Document rejected successfully', { variant: 'success' });
          setOpenRejectDialog(false);
          
          logSuccessAction({
            user: user._id,
            company: company || user.company, // Use modal company context or fallback to user company
            userDisplay: {
              name: user.displayName,
              email: user.email,
            },
            role: user.role,
            action: 'Rejected Employee Document',
            category: ACTION_CATEGORIES.USER_MANAGEMENT,
            details: `Successfully rejected document: ${getDocumentTypeName(currentDocument.requiredDocumentId)} for employee in store: ${company || user.company}. Reason: ${rejectionReason}`
          });
          
          
          // Refresh document list
          await refreshDocumentList();
        } else {
          throw new Error(response.error || 'Failed to reject document');
        }
      } catch (error) {
        console.error('Error rejecting document:', error);
        enqueueSnackbar('Failed to reject document', { variant: 'error' });
      } finally {
        setIsProcessing(false);
      }
    };
  
    const getDocumentTypeName = (docTypeId) => {
      // Look up the document name from our mapping
      return documentTypeMap[docTypeId] || '';
    };
  
    return (
      <>
        <Dialog
          open={open}
          onClose={onClose}
          fullWidth
          maxWidth="md"
          fullScreen={isMobile}
          aria-labelledby="document-review-dialog-title"
        >
          <DialogTitle id="document-review-dialog-title">
            Employee Document Review
            {company && (
              <Typography variant="subtitle2" color="textSecondary" sx={{ mt: 0.5 }}>
                Store: {company}
              </Typography>
            )}
            <IconButton
              aria-label="close"
              onClick={onClose}
              sx={{
                position: 'absolute',
                right: 8,
                top: 8,
                color: (theme) => theme.palette.grey[500],
              }}
            >
              <Icon icon="eva:close-fill" />
            </IconButton>
          </DialogTitle>
          <DialogContent dividers>
            {isLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                <CircularProgress />
              </Box>
            ) : documents.length === 0 && missingDocuments.length === 0 ? (
              <Typography variant="body1" align="center" sx={{ p: 3 }}>
                No documents found for this employee.
              </Typography>
            ) : documents.length === 0 && missingDocuments.length > 0 ? (
              <Box>
                <Typography variant="h6" align="center" sx={{ mb: 2 }}>
                  This employee has not uploaded any documents yet.
                </Typography>
                <Typography variant="body1" align="center" sx={{ mb: 4 }}>
                  The following documents need to be uploaded:
                </Typography>
                
                {missingDocuments.map(document => (
                  <Paper
                    key={document._id}
                    elevation={3}
                    sx={{
                      p: 2,
                      mb: 2,
                      borderLeft: '6px solid #9e9e9e'
                    }}
                  >
                    <Grid container spacing={2}>
                      <Grid item xs={12} md={7}>
                        <Typography variant="h6" gutterBottom>
                          {document.name}
                        </Typography>
                        
                        <Typography variant="body2" color="text.secondary" gutterBottom>
                          Status: 
                          <Chip
                            label="Not Submitted"
                            color="default"
                            size="small"
                            sx={{ ml: 1 }}
                          />
                        </Typography>
  
                        <Box mt={1}>
                          <Typography variant="body2" color="text.secondary">
                            File types allowed: {document.allowedFileTypes.join(', ')}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            Maximum file size: {document.maxFileSize} MB
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            Number of files required: {document.numberOfFiles}
                          </Typography>
                        </Box>
                      </Grid>
                    </Grid>
                  </Paper>
                ))}
              </Box>
            ) : (
              <>
                <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
                  <Tabs
                    value={tabValue}
                    onChange={handleTabChange}
                    variant={isMobile ? 'scrollable' : 'standard'}
                    scrollButtons="auto"
                    indicatorColor="primary"
                    textColor="primary"
                  >
                    <Tab label={`Approved (${documents.filter(doc => doc.status === 'approved').length})`} />
                    <Tab label={`Pending Review (${documents.filter(doc => doc.status === 'pending_approval').length})`} />
                    <Tab label={`Rejected (${documents.filter(doc => doc.status === 'rejected').length})`} />
                    <Tab label={`Expired (${documents.filter(doc => doc.status === 'expired').length})`} />
                    <Tab label={`Missing Documents (${missingDocuments.length})`} />
                  </Tabs>
                </Box>
  
                {/* Document List */}
                <Box sx={{ mt: 2 }}>
                  {tabValue === 4 ? (
                    // Missing Documents Tab
                    missingDocuments.length > 0 ? (
                      missingDocuments.map(document => (
                        <Paper
                          key={document._id}
                          elevation={3}
                          sx={{
                            p: 2,
                            mb: 2,
                            borderLeft: '6px solid #9e9e9e'
                          }}
                        >
                          <Grid container spacing={2}>
                            <Grid item xs={12} md={7}>
                              <Typography variant="h6" gutterBottom>
                                {document.name}
                              </Typography>
                              
                              <Typography variant="body2" color="text.secondary" gutterBottom>
                                Status: 
                                <Chip
                                  label="Not Submitted"
                                  color="default"
                                  size="small"
                                  sx={{ ml: 1 }}
                                />
                              </Typography>
  
                              <Box mt={1}>
                                <Typography variant="body2" color="text.secondary">
                                  File types allowed: {document.allowedFileTypes.join(', ')}
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                  Maximum file size: {document.maxFileSize} MB
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                  Number of files required: {document.numberOfFiles}
                                </Typography>
                              </Box>
                            </Grid>
                          </Grid>
                        </Paper>
                      ))
                    ) : (
                      <Typography variant="body1" align="center" sx={{ p: 3 }}>
                        Employee has submitted all required documents.
                      </Typography>
                    )
                  ) : (
                    // Existing tabs with submitted documents
                    documents
                      .filter(doc => {
                        if (tabValue === 0) return doc.status === 'approved';
                        if (tabValue === 1) return doc.status === 'pending_approval';
                        if (tabValue === 2) return doc.status === 'rejected';
                        if (tabValue === 3) return doc.status === 'expired';
                        if (tabValue === 4) return false;
                        return true;
                      })
                      .map(document => (
                        <Paper
                          key={document._id}
                          elevation={3}
                          sx={{
                            p: 2,
                            mb: 2,
                            borderLeft: `6px solid ${
                              document.status === 'approved' ? '#4caf50' : 
                              document.status === 'rejected' ? '#f44336' : 
                              document.status === 'expired' ? '#d32f2f' :
                              document.status === 'pending_approval' ? '#ff9800' :
                              '#ff9800'
                            }`
                          }}
                        >
                          <Grid container spacing={2}>
                            <Grid item xs={12} md={7}>
                              <Typography variant="h6" gutterBottom>
                                {getDocumentTypeName(document.requiredDocumentId)}
                              </Typography>
                              
                              <Typography variant="body2" color="text.secondary" gutterBottom>
                                Status: 
                                <Chip
                                  label={
                                    document.status === 'approved' ? 'Approved' : 
                                    document.status === 'rejected' ? 'Rejected' : 
                                    document.status === 'expired' ? 'Expired' :
                                    document.status === 'pending_approval' ? 'Pending Review' :
                                    'Pending Review'
                                  }
                                  color={
                                    document.status === 'approved' ? 'success' : 
                                    document.status === 'rejected' ? 'error' : 
                                    document.status === 'expired' ? 'error' :
                                    document.status === 'pending_approval' ? 'warning' :
                                    'warning'
                                  }
                                  size="small"
                                  sx={{ ml: 1 }}
                                />
                              </Typography>
  
                              <Typography variant="body2" color="text.secondary" gutterBottom>
                                Uploaded on: {new Date(document.createdAt).toLocaleString()}
                              </Typography>
  
                              {document.expirationDate && (
                                <Typography 
                                  variant="body2" 
                                  color={document.status === 'expired' ? "error.main" : "text.secondary"}
                                  gutterBottom
                                >
                                  Expires on: {new Date(document.expirationDate).toLocaleDateString()} 
                                  {document.status === 'expired' && <span> (EXPIRED)</span>}
                                </Typography>
                              )}
  
                              {document.reviewedAt && (
                                <Typography variant="body2" color="text.secondary" gutterBottom>
                                  Reviewed on: {new Date(document.reviewedAt).toLocaleString()}
                                </Typography>
                              )}
  
                              {document.rejectionReason && (
                                <Box mt={1} p={1} sx={{ backgroundColor: '#ffebee', borderRadius: 1 }}>
                                  <Typography variant="body2" color="error">
                                    Rejection Reason: {document.rejectionReason}
                                  </Typography>
                                </Box>
                              )}
                            </Grid>
  
                            <Grid item xs={12} md={5}>
                              <Typography variant="subtitle2" gutterBottom>
                                Files:
                              </Typography>
                              <List dense>
                                {document.files.map((file, index) => (
                                  <ListItem
                                    key={index}
                                    secondaryAction={
                                      <Button
                                        variant="outlined"
                                        size="small"
                                        onClick={() => handlePreview(file)}
                                        sx={{ minWidth: '100px', py: 1 }}
                                      >
                                        View
                                      </Button>
                                    }
                                    sx={{ pr: 16 }}
                                  >
                                    <ListItemText
                                      primary={file.name}
                                      secondary={`${(file.size / 1024 / 1024).toFixed(2)} MB`}
                                      sx={{
                                        wordBreak: 'break-word',
                                        whiteSpace: 'normal'
                                      }}
                                    />
                                  </ListItem>
                                ))}
                              </List>
  
                              {document.status === 'pending_approval' && (
                                <Stack direction={isMobile ? 'column' : 'row'} spacing={isMobile ? 1 : 2} mt={2}>
                                  <Button
                                    variant="contained"
                                    fullWidth={isMobile}
                                    color="primary"
                                    startIcon={<Icon icon="eva:checkmark-circle-2-outline" />}
                                    onClick={() => handleApprove(document)}
                                    disabled={isProcessing}
                                  >
                                    Approve
                                  </Button>
                                  <Button
                                    variant="outlined"
                                    color="error"
                                    startIcon={<Icon icon="eva:close-circle-outline" />}
                                    onClick={() => handleOpenRejectDialog(document)}
                                    disabled={isProcessing}
                                  >
                                    Reject
                                  </Button>
                                </Stack>
                              )}
                            </Grid>
                          </Grid>
                        </Paper>
                      ))
                  )}
                </Box>
              </>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={onClose} color="primary">
              Close
            </Button>
          </DialogActions>
        </Dialog>
  
        {/* File Preview Dialog */}
        <Dialog
          open={openPreviewDialog}
          onClose={() => setOpenPreviewDialog(false)}
          maxWidth="md"
          fullWidth
          fullScreen={isMobile}
        >
          <DialogTitle>
            File Preview
            <IconButton
              aria-label="close"
              onClick={() => setOpenPreviewDialog(false)}
              sx={{
                position: 'absolute',
                right: 8,
                top: 8,
                color: (theme) => theme.palette.grey[500],
                padding: '12px',  // Increase padding for larger touch area
              }}
            >
              <Icon icon="eva:close-fill" width={24} height={24} />
            </IconButton>
          </DialogTitle>
          <DialogContent>
            {previewFile && (
              <Box sx={{ textAlign: 'center' }}>
                {previewFile.type.includes('image') ? (
                  <img
                    src={previewFile.url}
                    alt={previewFile.name}
                    style={{ maxWidth: '100%', maxHeight: '70vh' }}
                  />
                ) : previewFile.type.includes('pdf') ? (
                  <iframe
                    src={previewFile.url}
                    title={previewFile.name}
                    width="100%"
                    height="500px"
                  />
                ) : (
                  <Typography variant="body1">
                    Preview not available for this file type. Click download to view the file.
                  </Typography>
                )}
              </Box>
            )}
          </DialogContent>
          <DialogActions>
            {previewFile && (
              <Button
                variant="contained"
                startIcon={<Icon icon="eva:download-fill" />}
                href={previewFile.url}
                download={previewFile.name}
                size="large"
                sx={{ py: 1.5, px: 3 }}
              >
                Download
              </Button>
            )}
            <Button 
              onClick={() => setOpenPreviewDialog(false)}
              size="large"
              sx={{ py: 1.5, px: 3 }}
            >
              Close
            </Button>
          </DialogActions>
        </Dialog>
  
        {/* Reject Dialog */}
        <Dialog
          open={openRejectDialog}
          onClose={handleCloseRejectDialog}
          maxWidth="sm"
          fullWidth
          fullScreen={isMobile}
        >
          <DialogTitle>
            Reject Document
            <IconButton
              aria-label="close"
              onClick={handleCloseRejectDialog}
              sx={{
                position: 'absolute',
                right: 8,
                top: 8,
                color: (theme) => theme.palette.grey[500],
                padding: '12px',  // Increase padding for larger touch area
              }}
            >
              <Icon icon="eva:close-fill" width={24} height={24} />
            </IconButton>
          </DialogTitle>
          <DialogContent>
            <Box sx={{ mt: 2 }}>
              <Typography variant="body2" gutterBottom>
                Please provide a reason for rejecting this document:
              </Typography>
              <TextField
                fullWidth
                multiline
                rows={4}
                value={rejectionReason}
                onChange={(e) => setRejectionReason(e.target.value)}
                placeholder="Enter detailed rejection reason to help the employee understand what needs to be fixed"
                sx={{ mt: 1 }}
              />
            </Box>
          </DialogContent>
          <DialogActions>
            <Button 
              onClick={handleCloseRejectDialog}
              size="large"
              sx={{ py: 1.5, px: 3 }}
            >
              Cancel
            </Button>
            <Button 
              variant="contained" 
              color="error" 
              onClick={handleReject}
              disabled={isProcessing || !rejectionReason.trim()}
              size="large"
              sx={{ py: 1.5, px: 3 }}
            >
              {isProcessing ? 'Processing...' : 'Reject Document'}
            </Button>
          </DialogActions>
        </Dialog>
      </>
    );
  }